import type { MovingRect } from './canvasAlignment'

/**
 * Die **Gruppe** (CONTEXT.md, Tauschpunkt): der zusammenhängende Klumpen, zu
 * dem ein Zettel gehört — und damit die Reichweite, in der die Geste am
 * **Tauschpunkt** noch ANDOCKT.
 *
 * > **Zwei Zettel gehören zusammen, solange zwischen sie kein weiterer passt**
 * > — höchstens eine **Zettellänge** Luft, auf beiden Achsen.
 *
 * Dieselbe Zahl trägt beides: Wer selbst noch zur Gruppe gehören WÜRDE, dockt
 * an; wer weiter draußen liegt, **verschiebt** (Hilfslinien, Einrasten, und
 * der Ursprung bleibt ein Loch). Eine zweite Zahl müsste man erklären.
 *
 * **Gemessen in den Maßen des GETRAGENEN**, nicht in denen der Gruppe: Er ist
 * es, der dazwischenpassen müsste. Eine breite Notiz hat damit einen größeren
 * Ring als ein kleiner Notiz — und genau das sieht man auch, denn die
 * **Leerplätze** haben ebenfalls seine Maße.
 *
 * Reines Rechenwerk ohne React, wie die übrigen `utils/`.
 */

/** Ein Element, wie es in die Gruppen-Rechnung geht. */
export interface GroupEntry {
  id: string
  rect: MovingRect
}

/** Der freie Raum zwischen zwei Lagen auf einer Achse — 0, wo sie sich überlappen. */
export function freeSpaceOn(a: MovingRect, b: MovingRect, axis: 'x' | 'y'): number {
  return axis === 'x'
    ? Math.max(b.x - (a.x + a.width), a.x - (b.x + b.width), 0)
    : Math.max(b.y - (a.y + a.height), a.y - (b.y + b.height), 0)
}

/** Ob zwischen zwei Lagen auf KEINER Achse mehr als eine Zettellänge Luft ist. */
const near = (a: MovingRect, b: MovingRect, size: { width: number; height: number }) =>
  freeSpaceOn(a, b, 'x') <= size.width && freeSpaceOn(a, b, 'y') <= size.height

/**
 * Die Gruppe um `id` — er selbst und alles, was über solche Nachbarschaften
 * erreichbar ist. Ein Zettel, der für sich steht, ist eine Gruppe aus einem.
 *
 * @param size die Maße des Getragenen — der Maßstab (siehe oben)
 */
export function groupAround(
  entries: readonly GroupEntry[],
  id: string,
  size: { width: number; height: number },
): MovingRect[] {
  const start = entries.find(entry => entry.id === id)
  if (!start) return []
  const rest = entries.filter(entry => entry.id !== id)
  const taken = new Set<string>([id])
  const group = [start.rect]
  const open = [start.rect]
  while (open.length > 0) {
    const current = open.pop()!
    for (const entry of rest) {
      if (taken.has(entry.id)) continue
      if (!near(current, entry.rect, size)) continue
      taken.add(entry.id)
      group.push(entry.rect)
      open.push(entry.rect)
    }
  }
  return group
}

/**
 * Ob eine Lage noch zur Gruppe **gehören würde** — und damit, ob dort noch
 * angedockt wird.
 */
export function belongsToGroup(
  rect: MovingRect,
  group: readonly MovingRect[],
  size: { width: number; height: number },
): boolean {
  return group.some(member => near(rect, member, size))
}
